import type { Project, GeranceStatusChange, GeranceMilestoneEvent } from '../types';
import type { RawMilestone } from './milestoneProgress';
import { STAGE_LABEL_TO_TAB } from './stages';
import { parseJSON } from '../components/ProjectForm';

// The Gérance calendar's time slider: how a project and its milestones stood
// at the end of a past day, rebuilt from what the app records as it goes —
// pipeline stage changes and milestone additions/cancellations in the project
// history, and each milestone's own start/due date history in the planning
// JSON. Nothing is stored for this view; it is a reading of those records.

/** 'YYYY-MM-DD' of a Date, in local time. */
export function toYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Local 'YYYY-MM-DD' of an ISO timestamp (a meeting at 23:30 belongs to its own day, not to UTC's). */
export function isoToLocalYMD(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : toYMD(d);
}

/** Start of day `ymd` (local) in ms. */
export function dayStartMs(ymd: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).getTime() : NaN;
}

/** End of day `ymd` (local) in ms — "as of that day" includes everything that happened during it. */
export function dayEndMs(ymd: string): number {
  return dayStartMs(ymd) + 86_400_000 - 1;
}

/** Adds `days` to a 'YYYY-MM-DD'. */
export function addDays(ymd: string, days: number): string {
  const d = new Date(dayStartMs(ymd));
  d.setDate(d.getDate() + days);
  return toYMD(d);
}

/** Whole days from `a` to `b` ('YYYY-MM-DD' each). */
export function daysBetween(a: string, b: string): number {
  return Math.round((dayStartMs(b) - dayStartMs(a)) / 86_400_000);
}

const ms = (iso: string) => new Date(iso).getTime();

/**
 * Pipeline stage label of a project at `asOfMs`: the last stage change before
 * then, else the stage it left in its first later change, else its current
 * one. Null when the project did not exist yet.
 */
export function stageLabelAt(project: Pick<Project, 'created_at'>, currentLabel: string, changes: GeranceStatusChange[], asOfMs: number): string | null {
  if (project.created_at && ms(project.created_at) > asOfMs) return null;
  let last: GeranceStatusChange | null = null;
  let next: GeranceStatusChange | null = null;
  for (const c of changes) {
    if (ms(c.at) <= asOfMs) last = c;
    else { next = c; break; }
  }
  if (last) return last.new_status ?? currentLabel;
  if (next) return next.old_status ?? currentLabel;
  return currentLabel;
}

/** Whether a pipeline stage label is one the Gérance calendar lists (Planning or Execution). */
export function isPlanningOrExecutionLabel(label: string | null): boolean {
  const tab = label ? STAGE_LABEL_TO_TAB[label] : undefined;
  return tab === 'planning' || tab === 'execution';
}

type DateHistory = RawMilestone['due_date_history'];

// A date field's value at `asOfMs`: the value the first later change
// replaced, else the current one (no later change → unchanged since).
function dateAt(history: DateHistory, current: string | null | undefined, asOfMs: number): string {
  const later = (history ?? []).find(h => ms(h.changed_at) > asOfMs);
  return (later ? later.old_date : current) || '';
}

/**
 * The milestones a project's plan held at `asOfMs`, with the start/end dates
 * they had then. A milestone added later is left out; one since cancelled is
 * back in if the cancellation came later. Order is the plan's own.
 */
export function milestonesAt(project: Pick<Project, 'planning'>, events: GeranceMilestoneEvent[], asOfMs: number): RawMilestone[] {
  const all: RawMilestone[] = parseJSON<any>(project.planning, {}).milestones;
  if (!Array.isArray(all)) return [];
  return all
    .filter(m => {
      if (!m?.id) return false;
      const own = events.filter(e => e.milestone_id === m.id);
      const added = own.find(e => e.kind === 'milestone_added');
      if (added && ms(added.at) > asOfMs) return false;
      // Cancelled/restored toggles: the last one at or before asOf decides;
      // none before → the current status stands.
      const toggles = own.filter(e => e.kind !== 'milestone_added' && ms(e.at) <= asOfMs);
      const lastToggle = toggles[toggles.length - 1];
      if (lastToggle) return lastToggle.kind === 'milestone_restored';
      const laterToggle = own.find(e => e.kind !== 'milestone_added');
      if (laterToggle) return laterToggle.kind === 'milestone_cancelled'; // cancelled later → existed then
      return m.status !== 'cancelled';
    })
    .map(m => ({
      ...m,
      estimated_start: dateAt(m.start_date_history, m.estimated_start, asOfMs),
      estimated_end: dateAt(m.due_date_history, m.estimated_end, asOfMs),
    }));
}

/**
 * Moments at which a project's plan (as milestonesAt reads it) changed: every
 * start/due date change and every milestone added, cancelled or restored.
 * Between two of these the plan is constant, so evaluating the plan at each
 * of them, plus at `fromMs`, visits every state the time slider can show,
 * which is what gives each row one fixed height for the whole slider range.
 */
export function planCheckpoints(project: Pick<Project, 'planning'>, events: GeranceMilestoneEvent[], fromMs: number): number[] {
  const all: RawMilestone[] = parseJSON<any>(project.planning, {}).milestones;
  const points = new Set<number>([fromMs]);
  const add = (iso: string | undefined | null) => {
    const t = iso ? ms(iso) : NaN;
    if (!Number.isNaN(t) && t > fromMs) points.add(t);
  };
  if (Array.isArray(all)) {
    for (const m of all) {
      for (const h of m?.due_date_history ?? []) add(h.changed_at);
      for (const h of m?.start_date_history ?? []) add(h.changed_at);
    }
  }
  for (const e of events) add(e.at);
  return [...points].sort((a, b) => a - b);
}

export type EventKind = 'meeting' | 'task';

export interface TimelineEvent {
  kind: EventKind;
  id: number | null;    // task id (opens the task) or meeting id; null when the task no longer exists
  title: string;
  by: string | null;    // organiser of the meeting, person who moved the task
  date: string;         // local 'YYYY-MM-DD'
  x: number;            // px on the timeline
}

/** One chip on a project's activity rail: one or more events of either kind, close together in time. */
export interface EventCluster {
  x: number;            // px of the first event: where the chip's first icon sits
  meetings: TimelineEvent[];
  tasks: TimelineEvent[];
}

// Chip geometry, shared with the CSS (.cal-chip): 1px border + 6px padding on
// each side, 12px icons, a count after an icon when it is above 1 (3px gap,
// about 6.5px per digit at 11px), 4px between the meeting and task halves.
export const CHIP_ICON_OFFSET = 13;   // chip left edge → centre of its first icon
const CHIP_MIN_GAP = 4;

function segmentWidth(count: number): number {
  return count === 0 ? 0 : 12 + (count > 1 ? 3 + Math.ceil(6.5 * String(count).length) : 0);
}

/** Estimated rendered width of a chip, in px. */
export function chipWidth(c: Pick<EventCluster, 'meetings' | 'tasks'>): number {
  const segs = [segmentWidth(c.meetings.length), segmentWidth(c.tasks.length)].filter(w => w > 0);
  return 14 + segs.reduce((a, b) => a + b, 0) + 4 * Math.max(0, segs.length - 1);
}

/**
 * Lays a project's events out as chips on its rail, left to right: an event
 * joins the current chip when its own chip would otherwise overlap it, else
 * starts a new one. A busy week becomes one chip ("3 meetings, 5 tasks")
 * instead of a smear of overlapping symbols, and chips never touch.
 */
export function clusterEvents(events: TimelineEvent[]): EventCluster[] {
  const sorted = [...events].sort((a, b) => a.x - b.x || a.date.localeCompare(b.date) || (a.kind === b.kind ? 0 : a.kind === 'meeting' ? -1 : 1));
  const out: EventCluster[] = [];
  let current: EventCluster | null = null;
  for (const ev of sorted) {
    if (current && ev.x - current.x < chipWidth(current) + CHIP_MIN_GAP) {
      (ev.kind === 'meeting' ? current.meetings : current.tasks).push(ev);
      continue;
    }
    current = { x: ev.x, meetings: [], tasks: [] };
    (ev.kind === 'meeting' ? current.meetings : current.tasks).push(ev);
    out.push(current);
  }
  return out;
}
